import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { customFieldsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { EntityType, DataType, CreateFieldConfigRequest, FieldConfiguration } from '@/types'
import { isValidRegexPattern } from '@/lib/utils'
import { usePermissions } from '@/contexts/PermissionContext'

export default function CustomFieldsPage() {
  const { canAddEdit } = usePermissions()
  const [activeTab, setActiveTab] = useState<EntityType>('CLIENT')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [formData, setFormData] = useState<CreateFieldConfigRequest>({
    field_label: '',
    entity_type: 'CLIENT',
    data_type: 'TEXT',
    is_mandatory: false,
    regex_pattern: '',
    dropdown_options: [],
  })
  const [dropdownInput, setDropdownInput] = useState('')
  const [regexError, setRegexError] = useState('')

  const queryClient = useQueryClient()

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['customFields', activeTab],
    queryFn: () => customFieldsApi.getAll(activeTab),
  })

  const createMutation = useMutation({
    mutationFn: customFieldsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] })
      setIsDialogOpen(false)
      resetForm()
      toast.success('Custom field created successfully')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create custom field'
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: customFieldsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customFields'] })
      toast.success('Custom field deleted successfully')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete custom field'
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({
      field_label: '',
      entity_type: activeTab,
      data_type: 'TEXT',
      is_mandatory: false,
      regex_pattern: '',
      dropdown_options: [],
    })
    setDropdownInput('')
    setRegexError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (formData.regex_pattern && !isValidRegexPattern(formData.regex_pattern)) {
      setRegexError('Invalid regex pattern')
      return
    }

    const payload: CreateFieldConfigRequest = {
      ...formData,
      entity_type: activeTab,
      regex_pattern: formData.regex_pattern || undefined,
      dropdown_options: formData.data_type === 'DROPDOWN' ? formData.dropdown_options : undefined,
    }

    createMutation.mutate(payload)
  }

  const addDropdownOption = () => {
    if (dropdownInput.trim()) {
      setFormData({
        ...formData,
        dropdown_options: [...(formData.dropdown_options || []), dropdownInput.trim()],
      })
      setDropdownInput('')
    }
  }

  const removeDropdownOption = (index: number) => {
    setFormData({
      ...formData,
      dropdown_options: formData.dropdown_options?.filter((_, i) => i !== index),
    })
  }

  const handleRegexChange = (value: string) => {
    setFormData({ ...formData, regex_pattern: value })
    if (value && !isValidRegexPattern(value)) {
      setRegexError('Invalid regex pattern')
    } else {
      setRegexError('')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Custom Fields Configuration</h1>
          <p className="text-muted-foreground">
            Configure custom fields for clients, businesses, and projects
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EntityType)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="CLIENT">Client Fields</TabsTrigger>
            <TabsTrigger value="BUSINESS">Business Fields</TabsTrigger>
            <TabsTrigger value="PROJECT">Project Fields</TabsTrigger>
            <TabsTrigger value="CARD">Card Fields</TabsTrigger>
          </TabsList>

          {canAddEdit('CUSTOM_FIELDS') && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open)
              if (open) resetForm()
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Custom Field
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-md">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>Add Custom Field</DialogTitle>
                  <DialogDescription>
                    Create a new custom field for {activeTab.toLowerCase()}s
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="field_label">Field Label *</Label>
                    <Input
                      id="field_label"
                      value={formData.field_label}
                      onChange={(e) => setFormData({ ...formData, field_label: e.target.value })}
                      placeholder="e.g., Company Size"
                      required
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="data_type">Data Type *</Label>
                    <Select
                      value={formData.data_type}
                      onValueChange={(v) => setFormData({ ...formData, data_type: v as DataType })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TEXT">Text</SelectItem>
                        <SelectItem value="NUMBER">Number</SelectItem>
                        <SelectItem value="DATE">Date</SelectItem>
                        <SelectItem value="DROPDOWN">Dropdown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.data_type === 'DROPDOWN' && (
                    <div className="grid gap-2">
                      <Label>Dropdown Options</Label>
                      <div className="flex gap-2">
                        <Input
                          value={dropdownInput}
                          onChange={(e) => setDropdownInput(e.target.value)}
                          placeholder="Add option"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              addDropdownOption()
                            }
                          }}
                        />
                        <Button type="button" variant="outline" onClick={addDropdownOption}>
                          Add
                        </Button>
                      </div>
                      {formData.dropdown_options && formData.dropdown_options.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.dropdown_options.map((option, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-md text-sm"
                            >
                              {option}
                              <button
                                type="button"
                                onClick={() => removeDropdownOption(index)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {formData.data_type === 'TEXT' && (
                    <div className="grid gap-2">
                      <Label htmlFor="regex_pattern">Regex Pattern (Optional)</Label>
                      <Input
                        id="regex_pattern"
                        value={formData.regex_pattern || ''}
                        onChange={(e) => handleRegexChange(e.target.value)}
                        placeholder="e.g., ^[A-Z]{2}[0-9]{6}$"
                      />
                      {regexError && (
                        <p className="text-sm text-destructive">{regexError}</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Switch
                      id="is_mandatory"
                      checked={formData.is_mandatory}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_mandatory: checked })}
                    />
                    <Label htmlFor="is_mandatory">Mandatory Field</Label>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || !!regexError}>
                    {createMutation.isPending ? 'Creating...' : 'Create Field'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        <TabsContent value="CLIENT" className="mt-4">
          <FieldsTable
            fields={fields}
            isLoading={isLoading}
            onDelete={(id) => deleteMutation.mutate(id)}
            isDeleting={deleteMutation.isPending}
            canAddEdit={canAddEdit('CUSTOM_FIELDS')}
          />
        </TabsContent>

        <TabsContent value="BUSINESS" className="mt-4">
          <FieldsTable
            fields={fields}
            isLoading={isLoading}
            onDelete={(id) => deleteMutation.mutate(id)}
            isDeleting={deleteMutation.isPending}
            canAddEdit={canAddEdit('CUSTOM_FIELDS')}
          />
        </TabsContent>

        <TabsContent value="PROJECT" className="mt-4">
          <FieldsTable
            fields={fields}
            isLoading={isLoading}
            onDelete={(id) => deleteMutation.mutate(id)}
            isDeleting={deleteMutation.isPending}
            canAddEdit={canAddEdit('CUSTOM_FIELDS')}
          />
        </TabsContent>

        <TabsContent value="CARD" className="mt-4">
          <FieldsTable
            fields={fields}
            isLoading={isLoading}
            onDelete={(id) => deleteMutation.mutate(id)}
            isDeleting={deleteMutation.isPending}
            canAddEdit={canAddEdit('CUSTOM_FIELDS')}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function FieldsTable({
  fields,
  isLoading,
  onDelete,
  isDeleting,
  canAddEdit,
}: {
  fields: FieldConfiguration[]
  isLoading: boolean
  onDelete: (id: string) => void
  isDeleting: boolean
  canAddEdit: boolean
}) {
  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (fields.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No custom fields configured. Click "Add Custom Field" to create one.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field Label</TableHead>
            <TableHead>Data Type</TableHead>
            <TableHead>Mandatory</TableHead>
            <TableHead>Options</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => (
            <TableRow key={field.config_id}>
              <TableCell className="font-medium">{field.field_label}</TableCell>
              <TableCell>{field.data_type}</TableCell>
              <TableCell>{field.is_mandatory ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                {field.dropdown_options?.join(', ') || '-'}
              </TableCell>
              <TableCell>
                {canAddEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(field.config_id)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

